import { Button } from '../components/ui/button';
import MetricCard from '../components/MetricCard';
import ConversionFunnel from '../components/ConversionFunnel';
import ReportCard from '../components/ReportCard';
import {
  Eye,
  Users,
  TrendingUp,
  ShoppingBag,
  BarChart3,
  Activity,
  PieChart,
  ArrowRight,
  Download,
  MapPin
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useState } from 'react';

export default function CampaignLanding() {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const handleDownloadCaseStudy = async () => {
    setIsGeneratingPDF(true);
    
    // TODO: Replace with actual case study PDF download
    // For now, show a placeholder message
    alert('Case study download will be available soon!');
    
    setIsGeneratingPDF(false);
  };
  // Sample data for conversion funnel
      const funnelData = [
        { name: 'Total Impressions', value: 4534000, percentage: '', convertRate: undefined, dropOffRate: undefined },
        { name: 'Machine Interactions', value: 71200, percentage: '', convertRate: '1.57% convert', dropOffRate: '98.43% drop off' },
        { name: 'Products Browsed', value: 8244, percentage: '', convertRate: '11.58% convert', dropOffRate: '88.42% drop off' },
        { name: 'Products Sold', value: 4355, percentage: '', convertRate: '52.83% convert', dropOffRate: '47.17% drop off' },
        { name: 'Subscriptions Generated', value: 155, percentage: '', convertRate: '3.56% convert', dropOffRate: '96.44% drop off' },
      ];

  const conversionRates = [
    { sku: 'Energy Drink Brand', rate: '44.6%' },
    { sku: 'Protein Drink Brand', rate: '38.2%' },
    { sku: 'Hydration Drink Brand', rate: '51.3%' },
  ];

  // Sample data for brand awareness uplift
  const brandAwarenessData = [
    { 
      metric: 'Brand Recognition',
      'Pre-Campaign': 32,
      'Post-Campaign': 67,
    },
    { 
      metric: 'Purchase Intent',
      'Pre-Campaign': 28,
      'Post-Campaign': 58,
    },
  ];

  const salesReportItems = [
    { label: 'Total units sold' },
    { label: 'Total units sold by SKU' },
    { label: 'Unit sales by time of day' },
    { label: 'Unit sales by day of week' },
    { label: 'Unit sales by day of month' },
    { label: 'Unit sales by location' },
    { label: 'Unit sales by region' },
    { label: 'Monthly ROS per SKU' },
  ];

  // Sample data for sales performance metrics
  const salesMetrics = [
    { 
      title: 'Total units sold', 
      icon: ShoppingBag
    },
    { 
      title: 'Total units sold by SKU', 
      icon: BarChart3
    },
    { 
      title: 'Unit sales by time of day', 
      icon: TrendingUp
    },
    { 
      title: 'Unit sales by day of week', 
      icon: Activity
    },
    { 
      title: 'Unit sales by day of month', 
      icon: PieChart
    },
    { 
      title: 'Unit sales by location', 
      icon: MapPin
    },
    { 
      title: 'Unit sales by region', 
      icon: Users
    },
    { 
      title: 'Monthly ROS per SKU', 
      icon: Eye
    },
  ];

  const marketshareData = [
    { label: 'In-unit Market Share', percentage: 32, subtext: 'your sales vs total unit sales' },
    { label: 'Category Market Share', percentage: 52, subtext: 'your sales vs category competitors sales' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-luxury-gradient opacity-5"></div>
        <div className="container mx-auto px-6 py-24 md:py-32 relative">
          {/* Logo at top left */}
          <div className="absolute top-6 left-6">
            <img 
              src="/bb_logo_colour.svg" 
              alt="Bright Blue" 
              className="h-8 md:h-10 w-auto"
            />
          </div>
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h1 className="text-5xl md:text-7xl font-bold text-brand-primary leading-tight">
              Unlock the Power of Listing and Retail Media
            </h1>
            <p className="text-xl md:text-2xl text-brand-dark max-w-3xl mx-auto leading-relaxed">
              Drive brand awareness, engagement, and measurable conversions with direct, data-backed access to your consumers.
            </p>
            <div className="mt-8">
              <Button 
                onClick={handleDownloadCaseStudy}
                disabled={isGeneratingPDF}
                variant="outline"
                className="bg-white text-brand-primary border-brand-primary hover:bg-brand-primary hover:text-white text-lg px-6 py-3 h-auto rounded-full shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50"
              >
                <Download className="mr-2 w-5 h-5" />
                {isGeneratingPDF ? 'Preparing...' : 'Download Case Study'}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Awareness Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-brand-primary mb-4">
              Machine-level Impressions and Engagement
            </h2>
            <p className="text-lg text-brand-dark max-w-2xl mx-auto">
              Reach your target audience with unparalleled visibility
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <MetricCard
              icon={Eye}
              value="45,000"
              label="Impressions per Month"
              description="Average per machine"
            />
            <MetricCard
              icon={Users}
              value="700"
              label="On-Screen Interactions per Month"
              description="Average per machine"
            />
            <MetricCard
              icon={TrendingUp}
              value="10-35"
              label="ROS per SKU"
              description="Per machine, per month"
            />
          </div>
        </div>
      </section>

      {/* Brand-in-Hand Section */}
      <section className="py-20 bg-brand-darker">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row items-center gap-12">
              <div className="flex-1 space-y-6">
                <h2 className="text-4xl md:text-5xl font-bold text-white">
                  Brand-in-Hand
                </h2>
                <p className="text-2xl text-brand-light-blue font-semibold">
                  Turn awareness into action.
                </p>
                <p className="text-lg text-white leading-relaxed">
                  Convert impressions into tangible results with in-unit sales directly to your target consumers.
                </p>
              </div>
              <div className="flex justify-center">
                <div className="p-8 bg-brand-primary rounded-2xl shadow-lg">
                  <ShoppingBag className="w-16 h-16 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Post-Campaign Reporting Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-brand-primary mb-4">
              Post-Campaign Reporting
            </h2>
                <p className="text-lg text-brand-dark max-w-2xl mx-auto">
                  The following three reports are provided post campaign for measurable results
                </p>
          </div>

          <div className="max-w-5xl mx-auto space-y-6">
            {/* Sales Performance Report */}
            <ReportCard
              icon={BarChart3}
              title="Sales Performance Report"
              description=""
              items={salesReportItems}
              value="sales"
              customContent={
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {salesMetrics.map((metric, index) => {
                    const Icon = metric.icon;
                    return (
                      <div key={index} className="bg-white rounded-lg p-6 border border-luxury-silver-light/40 hover:shadow-md transition-shadow">
                        <div className="flex flex-col items-center text-center space-y-4">
                          <div className="p-3 bg-luxury-silver-light rounded-lg">
                            <Icon className="w-6 h-6 text-brand-primary" />
                          </div>
                          <p className="text-lg font-semibold text-brand-dark">{metric.title}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              }
            />

            {/* Conversion Performance Report */}
            <ReportCard
              icon={Activity}
              title="Conversion Performance Report"
              description=""
              items={[]}
              value="conversion"
              customContent={
                <ConversionFunnel 
                  data={funnelData} 
                  conversionRates={conversionRates}
                />
              }
            />

            {/* Marketshare Report */}
            <ReportCard
              icon={PieChart}
              title="Marketshare Report"
              description=""
              items={[]}
              value="marketshare"
              customContent={
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {marketshareData.map((item, index) => (
                    <div key={index} className="text-center space-y-4">
                      <p className="text-lg font-semibold text-brand-dark">{item.label}</p>
                      <div className="relative w-48 h-48 mx-auto">
                        <svg className="transform -rotate-90 w-48 h-48">
                          <circle
                            cx="96"
                            cy="96"
                            r="80"
                            stroke="hsl(var(--luxury-silver-light))"
                            strokeWidth="16"
                            fill="none"
                          />
                          <circle
                            cx="96"
                            cy="96"
                            r="80"
                            stroke="hsl(var(--luxury-blue))"
                            strokeWidth="16"
                            fill="none"
                            strokeDasharray={`${item.percentage * 5.03} ${503 - item.percentage * 5.03}`}
                            strokeLinecap="round"
                            className="transition-all duration-1000"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-4xl font-bold text-brand-primary">{item.percentage}%</span>
                        </div>
                      </div>
                      <p className="text-sm text-brand-dark">{item.subtext}</p>
                    </div>
                  ))}
                </div>
              }
            />
          </div>
        </div>
      </section>

      {/* Brand Awareness Uplift Section */}
      <section className="py-20 bg-brand-dark">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Brand Awareness Uplift
            </h2>
            <p className="text-lg text-brand-light-blue max-w-2xl mx-auto">
              Measure the impact of your campaign with before and after insights
            </p>
          </div>

          <div className="max-w-5xl mx-auto bg-white rounded-2xl p-8 shadow-lg">
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={brandAwarenessData}
                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(220, 9%, 88%)" />
                <XAxis 
                  dataKey="metric" 
                  stroke="hsl(220, 9%, 46%)"
                  tick={{ fill: 'hsl(220, 9%, 28%)' }}
                />
                <YAxis 
                  stroke="hsl(220, 9%, 46%)"
                  tick={{ fill: 'hsl(220, 9%, 28%)' }}
                  label={{ value: 'Percentage (%)', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid hsl(220, 9%, 88%)',
                    borderRadius: '8px',
                    padding: '12px'
                  }}
                  formatter={(value: number) => `${value}%`}
                />
                <Legend />
                <Bar dataKey="Pre-Campaign" fill="hsl(220, 9%, 46%)" radius={[8, 8, 0, 0]} />
                <Bar dataKey="Post-Campaign" fill="hsl(217, 91%, 20%)" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>

            <div className="mt-8 text-center">
              <p className="text-sm text-brand-dark">
                Optional: Customer product survey available as add-on module
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-24 bg-brand-primary">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              Ready to grow your brand through smart retail media?
            </h2>
            <Button 
              size="lg"
              className="bg-white text-brand-primary hover:bg-brand-dark-blue hover:text-white text-lg px-8 py-6 h-auto rounded-full shadow-2xl hover:shadow-xl transition-all duration-300 hover:scale-105"
              asChild
            >
              <a href="https://retailmedia.apps.bright.blue" target="_blank" rel="noopener noreferrer">
                Build Your Campaign
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

